import { useCallback, useMemo } from "react";

import { getNumArray } from "@/lib/chillnn/get-num-array";
import dayjs from "@/lib/dayjs";
import type { InputMaybe, PriceRankItem } from "@/lib/gql/__generated__/graphql";

import type { ForAutoGenerator } from "@/components/features/_model/types/auto-generator-for-view-model";
import { useLoadingScreen } from "@/components/features/side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";

import type { FormOptionType } from "@/components/ui/form/_types/type";

import type { ProductRoomPriceRankDailyStatusFormModel } from "../../../../model/price-rank-and-daily-status/_shared/daily-stasus/type";
import type { ProductRoomPriceRankDailyStatusOnSetViewModelType } from "../../../../model/price-rank-and-daily-status/_shared/daily-stasus/use-case/use-room-price-rank-daily-status-model";

type Props = {
	dailyStatus: ProductRoomPriceRankDailyStatusFormModel["ViewModel"];
	onSetDailyStatus: ProductRoomPriceRankDailyStatusOnSetViewModelType;
	refetchRoomPriceRankDailyStatus: ({ startDt, endDt }: { startDt: string; endDt: string }) => Promise<void>;

	isDirty: boolean;
};

/**
 * YYYY-MM-DD の日付文字列から、その月の初日と最終日を取得する
 */
const getStartDtAndEndDt = (fullDate: string) => {
	return {
		startDt: dayjs(fullDate).startOf("month").format("YYYY-MM-DD"),
		endDt: dayjs(fullDate).endOf("month").format("YYYY-MM-DD"),
	};
};

export const useRoomPriceCalendar = ({
	dailyStatus,
	onSetDailyStatus,
	refetchRoomPriceRankDailyStatus,
	isDirty,
}: Props) => {
	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();

	const [year, month, _] = dailyStatus.statuses.value[0].date.value.split("-");

	// 日曜日からカレンダーが始まるので、1日までの空白を埋める
	const firstDay = dayjs(`${year}-${month}-01`).day();
	const emptyDays = Array.from({ length: firstDay }, (_, index) => index);

	const dailyStatuses = useMemo(
		() => [
			...emptyDays.map((_) => {
				return {
					date: {
						isEdited: false,
						value: "",
					},
					priceRankID: {
						isEdited: false,
						value: "",
					},
					hotelID: {
						isEdited: false,
						value: "",
					},
					color: {
						isEdited: false,
						value: "",
					},
				} satisfies ForAutoGenerator<
					{
						hotelID: string;
						date: string;
						color: string;
						priceRankID?: InputMaybe<string> | undefined;
					},
					""
				>;
			}),
			...dailyStatus.statuses.value,
		],
		[dailyStatus.statuses.value, emptyDays],
	);

	// 変更が保存されていない場合は確認ダイアログを表示
	const canRefetch = useCallback(() => {
		return isDirty ? window.confirm("変更が保存されていません\nこのページを離れると、変更が失われます") : true;
	}, [isDirty]);

	const selectableMonths: FormOptionType[] = useMemo(() => {
		// 1~12月までの選択肢を生成
		const months = Array.from({ length: 12 }, (_, index) => index + 1);
		return months.map((month) => {
			return {
				id: "selectableMonths_" + month.toString(),
				label: month.toString(),
				value: month.toString().padStart(2, "0"),
			} satisfies FormOptionType;
		});
	}, []);

	const selectableYears: FormOptionType[] = useMemo(() => {
		const currentYear = dayjs().year();
		// currentYear の 前後２年を含む 年の配列
		const yearArray = getNumArray(currentYear - 2, 1, 5);

		return yearArray.map((year) => {
			return {
				id: "selectableYears_" + year.toString(),
				label: year.toString(),
				value: year.toString(),
			} satisfies FormOptionType;
		});
	}, []);

	const handleSelectedMonthChange = useCallback(
		(value: string) => {
			// 変更が保存されていない場合は確認ダイアログを表示
			if (!canRefetch()) {
				return;
			}

			showLoadingScreenCallback();

			const YYYYMMDD = `${year}-${value.padStart(2, "0")}-01`;
			const { startDt, endDt } = getStartDtAndEndDt(YYYYMMDD);

			refetchRoomPriceRankDailyStatus({ startDt, endDt });
			hideLoadingScreenCallback();
		},
		[canRefetch, hideLoadingScreenCallback, refetchRoomPriceRankDailyStatus, showLoadingScreenCallback, year],
	);

	const handleSelectedYearChange = useCallback(
		(value: string) => {
			// 変更が保存されていない場合は確認ダイアログを表示
			if (!canRefetch()) {
				return;
			}

			showLoadingScreenCallback();

			const YYYYMMDD = `${value}-${month}-01`;
			const { startDt, endDt } = getStartDtAndEndDt(YYYYMMDD);

			refetchRoomPriceRankDailyStatus({ startDt, endDt });
			hideLoadingScreenCallback();
		},
		[canRefetch, hideLoadingScreenCallback, month, refetchRoomPriceRankDailyStatus, showLoadingScreenCallback],
	);

	const handleClickPrevMonth = useCallback(async () => {
		// 変更が保存されていない場合は確認ダイアログを表示
		if (!canRefetch()) {
			return;
		}

		const prevMonth = dayjs(`${year}-${month}-01`).subtract(1, "month").format("YYYY-MM-DD");
		const { startDt, endDt } = getStartDtAndEndDt(prevMonth);

		await refetchRoomPriceRankDailyStatus({ startDt, endDt });
	}, [canRefetch, month, refetchRoomPriceRankDailyStatus, year]);

	const handleClickNextMonth = useCallback(async () => {
		// 変更が保存されていない場合は確認ダイアログを表示
		if (!canRefetch()) {
			return;
		}

		const nextMonth = dayjs(`${year}-${month}-01`).add(1, "month").format("YYYY-MM-DD");
		const { startDt, endDt } = getStartDtAndEndDt(nextMonth);

		await refetchRoomPriceRankDailyStatus({ startDt, endDt });
	}, [canRefetch, month, refetchRoomPriceRankDailyStatus, year]);

	const onSetPriceRank = useCallback(
		({ selectedPriceRank, date }: { selectedPriceRank: PriceRankItem; date: string }) => {
			const newStatuses = dailyStatuses
				.filter((item) => !!item.date.value) // dailyStatusesの生成時に追加した空の日付を除外
				.map((status) => {
					if (status.date.value === date) {
						return {
							...status,
							priceRankID: {
								isEdited: true,
								value: selectedPriceRank.priceRankID,
							},
							color: {
								isEdited: true,
								value: selectedPriceRank.color,
							},
						};
					}

					return status;
				}) satisfies ProductRoomPriceRankDailyStatusFormModel["ViewModel"]["statuses"]["value"];

			const newValue = {
				isEdited: true,
				hasError: false,
				value: newStatuses,
			} satisfies ProductRoomPriceRankDailyStatusFormModel["ViewModel"]["statuses"];

			onSetDailyStatus([
				{
					key: "statuses",
					value: newValue,
				},
			]);
		},
		[dailyStatuses, onSetDailyStatus],
	);

	return {
		currentYear: year,
		currentMonth: month,
		dailyStatuses,

		selectableYears,
		handleSelectedYearChange,
		selectableMonths,
		handleSelectedMonthChange,

		handleClickPrevMonth,
		handleClickNextMonth,

		onSetPriceRank,
	};
};
